import React, { useMemo } from 'react';
import { useFeatureFlags, useFeatureFlagsLoading } from '@harnessio/ff-react-client-sdk';
import PropTypes from 'prop-types';
import ProgressBar from '../progressIndicator/component';

function parseFlags(flags) {
  const parsedFlags = {};
  for (const key in flags) {
    if (flags.hasOwnProperty(key)) {
      const value = flags[key];
      if (typeof value === 'boolean' || typeof value === 'number') {
        parsedFlags[key] = value;
      } else {
        try {
          parsedFlags[key] = JSON.parse(value);
        } catch (e) {
          parsedFlags[key] = value; // If parsing fails, keep the original value
        }
      }
    }
  }
  return parsedFlags;
}

const WithFeatureFlagsComponent = React.memo(({ WrappedComponent, componentProps }) => {
  const flags = useFeatureFlags();
  const loading = useFeatureFlagsLoading();
  const parsedFlags = useMemo(() => parseFlags(flags), [flags]);

  // This condition prevents rendering of the wrapped component until the flags are available.
  if (Object.keys(flags).length === 0) {
    return <ProgressBar />;
  }

  return <WrappedComponent flags={parsedFlags} loading={loading} {...componentProps} />;
});

WithFeatureFlagsComponent.displayName = 'WithFeatureFlagsComponent';

WithFeatureFlagsComponent.propTypes = {
  WrappedComponent: PropTypes.elementType.isRequired,
  componentProps: PropTypes.object.isRequired,
};

const withFlags = WrappedComponent => {
  const WithFeatureFlags = props => (
    <WithFeatureFlagsComponent WrappedComponent={WrappedComponent} componentProps={props} />
  );
  WithFeatureFlags.displayName = `WithFeatureFlags(${WrappedComponent.displayName ||
    WrappedComponent.name ||
    'Component'})`;
  return WithFeatureFlags;
};

export default withFlags;